import { __awaiter } from "tslib";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from '../types';
import { MISSING_CUSTOMER } from '@open-tender/types';
import { checkAuth, selectToken } from './account';
import { showNotification } from '../notifications';
const initialState = {
    entities: [],
    error: null,
    loading: 'idle',
    lookup: {}
};
export var CustomerGiftCardsActionType;
(function (CustomerGiftCardsActionType) {
    CustomerGiftCardsActionType["FetchCustomerGiftCards"] = "customer/fetchCustomerGiftCards";
    CustomerGiftCardsActionType["RemoveCustomerGiftCard"] = "customer/removeCustomerGiftCard";
    CustomerGiftCardsActionType["AddCustomerGiftCard"] = "customer/addCustomerGiftCard";
    CustomerGiftCardsActionType["AssignCustomerGiftCard"] = "customer/assignCustomerGiftCard";
    CustomerGiftCardsActionType["AssignCustomerGiftCardOther"] = "customer/assignCustomerGiftCardOther";
    CustomerGiftCardsActionType["UpdateCustomerGiftCard"] = "customer/updateCustomerGiftCard";
})(CustomerGiftCardsActionType || (CustomerGiftCardsActionType = {}));
export const fetchCustomerGiftCards = createAsyncThunk(CustomerGiftCardsActionType.FetchCustomerGiftCards, (_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        return yield api.getCustomerGiftCards(token);
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const updateCustomerGiftCard = createAsyncThunk(CustomerGiftCardsActionType.UpdateCustomerGiftCard, ({ giftCardId, data, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.putCustomerGiftCard(token, giftCardId, data);
        const giftCards = yield api.getCustomerGiftCards(token);
        dispatch(showNotification('Gift card balance updated!'));
        if (callback)
            callback();
        return giftCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const removeCustomerGiftCard = createAsyncThunk(CustomerGiftCardsActionType.RemoveCustomerGiftCard, ({ giftCardId, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.deleteCustomerGiftCard(token, giftCardId);
        const giftCards = yield api.getCustomerGiftCards(token);
        dispatch(showNotification('Gift card removed!'));
        if (callback)
            callback();
        return giftCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const addCustomerGiftCard = createAsyncThunk(CustomerGiftCardsActionType.AddCustomerGiftCard, ({ data, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.postCustomerGiftCard(token, data);
        const giftCards = yield api.getCustomerGiftCards(token);
        dispatch(showNotification('Gift card added!'));
        if (callback)
            callback();
        return giftCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const assignCustomerGiftCard = createAsyncThunk(CustomerGiftCardsActionType.AssignCustomerGiftCard, ({ cardNumber, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.postCustomerGiftCardAssign(token, cardNumber);
        const giftCards = yield api.getCustomerGiftCards(token);
        dispatch(showNotification('Gift card added to your account!'));
        if (callback)
            callback();
        return giftCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const assignCustomerGiftCardOther = createAsyncThunk(CustomerGiftCardsActionType.AssignCustomerGiftCardOther, ({ giftCardId, email, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.postCustomerGiftCardAssignOther(token, giftCardId, email);
        const giftCards = yield api.getCustomerGiftCards(token);
        dispatch(showNotification(`Gift card assigned to ${email}`));
        if (callback)
            callback();
        return giftCards;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const customerGiftCardsSlice = createSlice({
    name: ReducerType.GiftCards,
    initialState,
    reducers: {
        resetCustomerGiftCards: () => initialState,
        resetCustomerGiftCardsError: state => {
            state.error = null;
            state.loading = 'idle';
        },
        setCustomerGiftCards: (state, action) => {
            state.entities = action.payload;
            state.error = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchCustomerGiftCards.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomerGiftCards.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomerGiftCards.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(removeCustomerGiftCard.fulfilled, (state, action) => {
            state.loading = 'idle';
            state.error = null;
            state.entities = action.payload;
        })
            .addCase(removeCustomerGiftCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(removeCustomerGiftCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(addCustomerGiftCard.fulfilled, (state, action) => {
            state.loading = 'idle';
            state.error = null;
            state.entities = action.payload;
        })
            .addCase(addCustomerGiftCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(addCustomerGiftCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(updateCustomerGiftCard.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(updateCustomerGiftCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(updateCustomerGiftCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(assignCustomerGiftCardOther.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(assignCustomerGiftCardOther.pending, state => {
            state.loading = 'pending';
        })
            .addCase(assignCustomerGiftCardOther.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(assignCustomerGiftCard.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(assignCustomerGiftCard.pending, state => {
            state.loading = 'pending';
        })
            .addCase(assignCustomerGiftCard.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetCustomerGiftCards, resetCustomerGiftCardsError, setCustomerGiftCards } = customerGiftCardsSlice.actions;
export const selectCustomerGiftCards = (state) => state.customer.giftCards;
export const customerGiftCardsReducer = customerGiftCardsSlice.reducer;
